import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../../../api/endpoints/platform';
import { SELECT_CLASS } from '../../../lib/selectClass';
import { shortId } from '../../../lib/shortId';
import { Label } from '../../../components/ui/label';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import type { AssignPlatformRoleRequestLocal } from '../../../api/types/PlatformLocal';

export type ScopeMode = 'GLOBAL' | 'TENANTS';

/**
 * Estado del selector de rol + scope. `roleId === ''` = "sin rol" (válido solo donde el rol es
 * opcional, ej. el alta de admin). En modo TENANTS, `tenantIds` son los tenants elegidos.
 */
export interface RoleScopeValue {
    mode: ScopeMode;
    roleId: string;
    tenantIds: string[];
}

/** Estado inicial: modo global, sin rol elegido. */
export const emptyRoleScope: RoleScopeValue = { mode: 'GLOBAL', roleId: '', tenantIds: [] };

/**
 * Traduce el estado del picker a los assigns `{ roleId, scopeRef }` que se mandan al backend
 * (`POST .../role-assignments`). Mismo patrón que tenía inline el AssignRoleDialog:
 *  - sin rol (`roleId === ''`) → `[]` (no se asigna nada).
 *  - GLOBAL → un assign con `scopeRef: '*'`.
 *  - TENANTS → un assign por tenant elegido, cada uno con `scopeRef: tenantId`.
 */
export function buildAssigns(value: RoleScopeValue): AssignPlatformRoleRequestLocal[] {
    if (!value.roleId) return [];
    if (value.mode === 'GLOBAL') {
        return [{ roleId: value.roleId, scopeRef: '*' }];
    }
    return value.tenantIds.map((tenantId) => ({ roleId: value.roleId, scopeRef: tenantId }));
}

/** ¿El estado actual produce assigns aplicables (rol elegido + scope válido)? */
export function hasValidAssigns(value: RoleScopeValue): boolean {
    return !!value.roleId && (value.mode === 'GLOBAL' || value.tenantIds.length > 0);
}

interface Props {
    value: RoleScopeValue;
    onChange: (next: RoleScopeValue) => void;
    /** Habilita las queries de roles/tenants (no pegar hasta que el contenedor esté abierto). */
    enabled?: boolean;
    /**
     * Texto de la opción vacía del select de rol. En contextos donde el rol es OPCIONAL (alta de
     * admin) se ofrece como "Sin rol"; donde es obligatorio (AssignRoleDialog) el contenedor
     * deshabilita el submit hasta elegir uno, y acá se muestra como placeholder.
     */
    emptyOptionLabel?: string;
    /** Prefijo para los `id`/`htmlFor` (evita colisión si hubiera dos pickers en una página). */
    idPrefix?: string;
}

/**
 * Selector reutilizable de ROL + SCOPE para asignar a un admin platform. Extraído del AssignRoleDialog
 * para reusarlo también en el alta (`PlatformAdminForm`). Modela el scope con un toggle GLOBAL / por-tenant
 * (el backend RECALCULA el effective scope desde el rol — 220f7b4): global → scopeRef '*'; por-tenant →
 * un assign por tenant con scopeRef = tenantId. Es CONTROLADO (value/onChange): el contenedor decide si
 * el rol es opcional y cómo se traducen los assigns (ver `buildAssigns`).
 */
export function RoleScopePicker({
    value,
    onChange,
    enabled = true,
    emptyOptionLabel = 'Elige un rol',
    idPrefix = 'rsp',
}: Props) {
    const { data: rolesData } = useQuery({
        queryKey: ['platform', 'roles'],
        queryFn: () => platformApi.listPlatformRoles(),
        enabled,
    });
    const { data: tenantsData } = useQuery({
        queryKey: ['platform', 'tenants'],
        queryFn: () => platformApi.listTenants(),
        enabled: enabled && value.mode === 'TENANTS',
    });

    const roles = useMemo(() => rolesData?.items ?? [], [rolesData]);
    const tenants = useMemo(() => tenantsData?.items ?? [], [tenantsData]);

    const setMode = (mode: ScopeMode): void => {
        onChange({ ...value, mode });
    };
    const setRoleId = (roleId: string): void => {
        onChange({ ...value, roleId });
    };
    const toggleTenant = (tenantId: string): void => {
        const tenantIds = value.tenantIds.includes(tenantId)
            ? value.tenantIds.filter((id) => id !== tenantId)
            : [...value.tenantIds, tenantId];
        onChange({ ...value, tenantIds });
    };

    const roleSelectId = `${idPrefix}-role`;

    return (
        <div className="space-y-4">
            {/* Toggle de alcance */}
            <fieldset className="space-y-2">
                <legend className="flex items-center gap-1 text-sm font-medium">
                    Alcance
                    <HelpHint text={HELP_TEXT.scope} />
                </legend>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="radio"
                        name={`${idPrefix}-scope-mode`}
                        checked={value.mode === 'GLOBAL'}
                        onChange={() => {
                            setMode('GLOBAL');
                        }}
                    />
                    Acceso global (todos los tenants)
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="radio"
                        name={`${idPrefix}-scope-mode`}
                        checked={value.mode === 'TENANTS'}
                        onChange={() => {
                            setMode('TENANTS');
                        }}
                    />
                    Tenants específicos
                </label>
            </fieldset>

            {/* Rol */}
            <div className="space-y-1">
                <div className="flex items-center gap-1">
                    <Label htmlFor={roleSelectId}>Rol</Label>
                    <HelpHint text={HELP_TEXT.role} />
                </div>
                <select
                    id={roleSelectId}
                    className={SELECT_CLASS}
                    value={value.roleId}
                    onChange={(e) => {
                        setRoleId(e.target.value);
                    }}
                >
                    <option value="">{emptyOptionLabel}</option>
                    {roles.map((r) => (
                        <option key={r.roleId} value={r.roleId}>
                            {r.displayName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Multiselect de tenants (solo modo TENANTS) */}
            {value.mode === 'TENANTS' && (
                <div className="space-y-2">
                    <Label>Tenants</Label>
                    <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                        {tenants.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No hay tenants para mostrar.
                            </p>
                        ) : (
                            tenants.map((tn) => (
                                <label
                                    key={tn.tenantId}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        checked={value.tenantIds.includes(tn.tenantId)}
                                        onChange={() => {
                                            toggleTenant(tn.tenantId);
                                        }}
                                    />
                                    {tn.displayName}
                                    {/* Desambiguador para tenants con el mismo nombre: id ACORTADO,
                                        nunca el UUID completo. */}
                                    <span className="text-muted-foreground">
                                        ({shortId(tn.tenantId)})
                                    </span>
                                </label>
                            ))
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Se crea una asignación por cada tenant elegido, acotada a ese tenant.
                    </p>
                </div>
            )}
        </div>
    );
}
